import { createLightTheme, type BrandVariants, type Theme } from '@fluentui/react-components';

/** Trinity brand navy — same primary used elsewhere (e.g. the admin dashboard's token chart). */
const BRAND_PRIMARY = '#050742';

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(n: number): string {
  return Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
}

function mixHex(from: [number, number, number], to: [number, number, number], t: number): string {
  const r = from[0] + (to[0] - from[0]) * t;
  const g = from[1] + (to[1] - from[1]) * t;
  const b = from[2] + (to[2] - from[2]) * t;
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Builds a full 16-step Fluent brand ramp from a single base color, anchored at shade 80 (the
 * step Fluent's light theme uses for `colorBrandBackground` and most other "primary" tokens —
 * see @fluentui/tokens' alias/lightColor.js). Shades below 80 interpolate toward white (used for
 * hover/pressed states on brand-colored surfaces, and lighter brand accents); shades above 80
 * interpolate toward black (used for on-brand text/icons and darker accents). This is a plain
 * linear RGB interpolation, not Fluent's own perceptual ramp generator — good enough for a single
 * task-pane app's button/link colors, not intended to reproduce their exact accessibility ramp.
 */
function buildBrandRamp(base: string): BrandVariants {
  const rgb = hexToRgb(base);
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [0, 0, 0];
  const steps = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160] as const;

  const ramp = {} as BrandVariants;
  for (const step of steps) {
    if (step === 80) {
      ramp[step] = base;
    } else if (step < 80) {
      ramp[step] = mixHex(white, rgb, step / 80);
    } else {
      ramp[step] = mixHex(rgb, black, (step - 80) / 80);
    }
  }
  return ramp;
}

export const qcbotLightTheme: Theme = createLightTheme(buildBrandRamp(BRAND_PRIMARY));
